<!--
  THE ENTITY CHAT (ZS ENTITY, plan 3b) — Spotlight's bar, dock and card, so both studio chats look the same.
  ⚠ No HTML comment may sit inside a start tag (it breaks the template parse); comments go above elements.
-->
<!-- Plan 4b — the held action in Spotlight's confirm bar (blue or red, Cancel rejects); rightInset keeps the composer and card clear of the docked dock (N19). -->
<!-- Plan 4c (Q42) — Stop only while the model is thinking; a save or an approved action can't be stopped. -->
<!-- Was (2026-10-04, plan 5 — the panel is always 360px wide, no docked/floating mode): [rightInset]="dockTenant && dockMode === 'docked' ? 320 : 0" -->
<!-- Was (2026-10-05, plan 6 Q54 — the strip's right corner is now kept for the panel button): [rightInset]="dockTenant ? 360 : 0" -->
<!-- Was (2026-10-05, nav 7b T5 review, C1 — the bar must also show busy while the navigator is being asked): [busy]="session.busy" -->
<!-- Was (2026-10-05, nav 8a Task 4 — the navigator's own question now takes the slot): [cardTemplate]="cardTpl" -->
<!-- The strip follows the Entities page's sun/moon switch: white on the white page, today's dark bar on the dark one. -->
<app-spotlight-bottom-bar
  [showBack]="false"
  [showAtlas]="false"
  [showForge]="false"
  placeholder="Ask about your entities"
  [crumbs]="crumbs"
  [busy]="session.busy || routing"
  [inputText]="input"
  [progressLine]="session.busy ? session.working : ''"
  [progressSeconds]="progressSeconds"
  [unsavedCount]="unsaved"
  [cardTemplate]="navAsks ? navCardTpl : cardTpl"
  [confirming]="!!session.pendingAction"
  [confirmLabel]="session.pendingAction?.label ?? ''"
  [confirmAction]="session.pendingAction?.actionWord ?? ''"
  [confirmTone]="session.pendingAction?.tone ?? 'red'"
  rejectLabel="Cancel"
  [rightInset]="dockTenant ? 360 : 168"
  [stoppable]="session.working === 'Thinking'"
  [light]="!pageThemeDark"
  (submit)="onSubmit($event)"
  (inputTextChange)="input = $event"
  (cancelTurn)="session.cancel()"
  (navigate)="openDock('tree')"
  (confirmApprove)="approve()"
  (confirmReject)="session.reject()">
</app-spotlight-bottom-bar>

<!--
  Plan 6 (spec Q54) — the panel's own button. The bar's "Entities" crumb alone was too hard to find. It sits in the
  strip's right corner — under the panel when it is open, in the space rightInset keeps clear when it is closed — and
  opens or closes the panel.
-->
<!-- Was (2026-10-15): [ngClass]="dockTenant ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-gray-900 text-gray-50 hover:bg-gray-700'" — open stays blue on either page. -->
<button type="button" id="zs-entity-panel-toggle" (click)="togglePanel()"
  class="fixed bottom-2 right-3 z-[210] flex h-8 items-center gap-2 rounded-full px-3 text-xs font-medium shadow-sm transition-colors duration-150"
  [ngClass]="dockTenant ? 'bg-blue-600 text-white hover:bg-blue-500' : !pageThemeDark ? 'bg-white text-gray-800 hover:bg-gray-100' : 'bg-gray-900 text-gray-50 hover:bg-gray-700'"
  [attr.aria-pressed]="!!dockTenant"
  [title]="dockTenant ? 'Hide the panel' : 'Show your entities, their fields and the conversation'">
  <svg class="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="2"></rect><path d="M15 4v16"></path>
  </svg>
  <span>{{ dockTenant ? 'Hide panel' : 'Entities panel' }}</span>
</button>

<!-- Plan 5 (spec Q50–Q51): the entity chat's own right panel replaces Spotlight's dock here. The old dock markup is kept, verbatim, in was-spotlight-dock.html.txt beside this file. -->
<!-- The panel follows the Entities page's sun/moon switch with the strip, so a white page never carries a dark panel. -->
<app-entity-panel *ngIf="dockTenant"
  [page]="page"
  [messages]="session.messages"
  [unsaved]="unsaved ?? 0"
  [tab]="dockTenant === 'updates' ? 'conversation' : 'entities'"
  [focus]="panelFocus"
  [light]="!pageThemeDark"
  (tabChange)="dockTenant = $event === 'conversation' ? 'updates' : 'tree'"
  (closed)="dockTenant = null"
  (fieldPicked)="pickFromPanel($event)"
  (openEntity)="openOnPage($event)"
  (editField)="page.editField($event.path, $event.name)"
  (prompt)="input = $event">
</app-entity-panel>

<!--
  The reply card, rendered by the bar inside the composer's own box (Spotlight's alignment rule).
  ⚠ Its root classes are Spotlight's card classes, pinned by tools/zs-entity-shell-tests.mjs test 6 — the layout classes
  plus the dark side of the tone ternary, which is how the dark page still gets exactly Spotlight's card.
-->
<!-- Was (2026-10-15), before the white variant — the colours sat in the class: the card bg-gray-900 border-gray-700
  text-gray-200; its dividers border-gray-800; the question text-gray-100; dismiss text-gray-500 hover:text-white;
  a choice text-gray-100 hover:bg-gray-800/60; the conversation link text-blue-400 hover:text-blue-300. -->
<ng-template #cardTpl>
  <div id="zs-entity-card" *ngIf="session.card && dockTenant !== 'updates'"
    class="absolute bottom-full left-0 right-0 mb-2 z-[211] max-h-[60vh]
           border rounded-lg shadow-2xl text-sm
           flex flex-col overflow-hidden"
    [ngClass]="!pageThemeDark ? 'bg-white border-gray-200 text-gray-800' : 'bg-gray-900 border-gray-700 text-gray-200'">
    <div class="shrink-0 flex items-start gap-2 px-3 pt-2.5 pb-2 border-b" [ngClass]="!pageThemeDark ? 'border-gray-200' : 'border-gray-800'">
      <div class="flex-1 min-w-0">
        <div *ngIf="session.card.question" class="text-xs mt-0.5 whitespace-pre-line max-h-[30vh] overflow-y-auto"
          [ngClass]="!pageThemeDark ? 'text-gray-800' : 'text-gray-100'">{{ session.card.question }}</div>
        <div *ngIf="!session.card.question" class="text-[11px] text-gray-500 truncate">Entity chat</div>
      </div>
      <button (click)="session.dismiss()" title="Dismiss"
        class="shrink-0 text-sm leading-none" [ngClass]="!pageThemeDark ? 'text-gray-500 hover:text-gray-900' : 'text-gray-500 hover:text-white'">&times;</button>
    </div>
    <div class="min-h-0 overflow-y-auto">
      <div *ngIf="session.card.text" class="px-3 py-2.5 whitespace-pre-wrap">{{ session.card.text }}</div>
      <div *ngIf="session.card.options.length" class="flex flex-col">
        <button *ngFor="let o of session.card.options" (click)="choose(o.id)"
          class="w-full text-left px-3 py-2 text-xs transition-colors"
          [ngClass]="!pageThemeDark ? 'text-gray-800 hover:bg-gray-100' : 'text-gray-100 hover:bg-gray-800/60'">{{ o.label }}</button>
      </div>
    </div>
    <!-- Nav 8a (Q18b) — "Or go to:" after a turn that found nothing here. -->
    <!-- Was (2026-10-15): <app-nav-choices lane="entity" variant="row"></app-nav-choices> -->
    <app-nav-choices lane="entity" variant="row" [light]="!pageThemeDark"></app-nav-choices>
    <div class="shrink-0 flex items-center justify-between gap-2 border-t px-3 py-1.5" [ngClass]="!pageThemeDark ? 'border-gray-200' : 'border-gray-800'">
      <!-- Was (2026-10-04, plan 5 T3 polish, C1 — the link named the old dock tenant, not the panel tab it opens): updates &rsaquo; -->
      <button (click)="openDock('updates')" class="text-[10px]"
        [ngClass]="!pageThemeDark ? 'text-blue-600 hover:text-blue-700' : 'text-blue-400 hover:text-blue-300'">conversation &rsaquo;</button>
      <div class="flex items-center gap-2">
        <!-- Was (2026-10-03, plan 4a — N16, Undo vanished once another reply replaced the card): *ngIf="session.card.undo && session.canUndo()" -->
        <!-- Was (2026-10-03, plan 4b — "this" named the card's request even on cards that changed nothing, and Undo sat beside held choices):
        <button *ngIf="session.canUndo()" (click)="session.undo()"
          class="text-[10px] text-gray-300 hover:text-white">Undo this</button>
        -->
        <!-- Was (2026-10-03, plan 4b final review — M1, Undo must never sit beside a held action's card): *ngIf="session.canUndo() && !session.card.options.length" -->
        <!-- Was (2026-10-15): Undo's text-gray-300 hover:text-white, and Save's border-emerald-400/80 bg-emerald-500/40
          text-emerald-100 hover:bg-emerald-500/50 hover:text-white, sat in their class. -->
        <button *ngIf="session.canUndo() && !session.card.options.length && !session.card.held" (click)="session.undo()"
          class="text-[10px]" [ngClass]="!pageThemeDark ? 'text-gray-600 hover:text-gray-900' : 'text-gray-300 hover:text-white'">{{ session.card.undo ? 'Undo this' : 'Undo last change' }}</button>
        <button *ngIf="session.card.save" (click)="saveNow()"
          class="px-2.5 py-0.5 rounded-md text-[11px] font-semibold border"
          [ngClass]="!pageThemeDark ? 'border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-emerald-400/80 bg-emerald-500/40 text-emerald-100 hover:bg-emerald-500/50 hover:text-white'">{{ session.card.save }}</button>
      </div>
    </div>
  </div>
</ng-template>

<!-- Nav 8a (Q18a) — the navigator's own question in this chat ("Which do you mean?"), with buttons. -->
<!-- Was (2026-10-15): <ng-template #navCardTpl><app-nav-choices lane="entity" variant="card"></app-nav-choices></ng-template> -->
<ng-template #navCardTpl><app-nav-choices lane="entity" variant="card" [light]="!pageThemeDark"></app-nav-choices></ng-template>
